import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { getTimeline } from './core/api'
import { Link } from 'react-router-dom'
import { DASHBOARD_ROUTE, TIMELINE_BREADCRUMBS } from './core/consts'
import { DEFAULT_CURRENCY, DEFAULT_STEP_MS, DEFAULT_VIEW, WINDOW_MS } from './core/query.defaults'
import { TimelineResponse } from './core/response.types'
import { TimelineView } from './core/types'
import { visibleDeposits, visiblePayouts } from './core/view'
import TimelineHeader from './components/TimelineHeader'
import TimelineCanvas from './components/TimelineCanvas'
import TimelineWindowNav from './components/TimelineWindowNav'

const LOAD_ERROR = 'Не удалось загрузить таймлайн'

const DepositPayoutTimelinePage: React.FC = () => {
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY)
  const [toMs, setToMs] = useState(() => Date.now())
  const [stepMs, setStepMs] = useState(DEFAULT_STEP_MS)
  const [view, setView] = useState<TimelineView>(DEFAULT_VIEW)
  const [data, setData] = useState<TimelineResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fromMs = toMs - WINDOW_MS

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getTimeline({
        currency,
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
      })
      if (res.data.success && res.data.data) {
        setData(res.data.data)
      } else {
        setError(LOAD_ERROR)
      }
    } catch {
      setError(LOAD_ERROR)
    } finally {
      setLoading(false)
    }
  }, [currency, fromMs, toMs])

  useEffect(() => {
    load()
  }, [load])

  // Ids of another currency's window would silently hide everything.
  const changeCurrency = (next: string) => {
    setView((v) => ({
      ...v,
      payoutMerchantIds: [],
      payoutMethodIds: [],
      depositMerchantIds: [],
      depositMethodIds: [],
    }))
    setCurrency(next)
  }

  // The window never moves past "now": the future has no data.
  const shift = (dir: -1 | 1) => setToMs((t) => Math.min(t + dir * stepMs, Date.now()))

  const payouts = useMemo(() => (data ? visiblePayouts(data, view) : []), [data, view])
  const deposits = useMemo(() => (data ? visibleDeposits(data, view) : []), [data, view])

  return (
    <>
      <PageTitle breadcrumbs={TIMELINE_BREADCRUMBS}>Таймлайн депозитов к выплатам</PageTitle>

      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
        <div>
          <div className='text-muted fs-8 text-uppercase fw-semibold'>Состояние мэтчинга</div>
          <h2 className='fw-bold mb-1'>Таймлайн депозитов к выплатам</h2>
          <div className='text-muted fs-7'>
            Выплаты — бары на время жизни в пуле, депозиты — точки в момент попытки смэтчиться. Клик
            по точке подсвечивает выплаты, с которыми депозит сравнивался.
          </div>
        </div>
        <Link to={DASHBOARD_ROUTE} className='btn btn-sm btn-light'>
          ← Обзор
        </Link>
      </div>

      <TimelineHeader
        data={data}
        currency={currency}
        onCurrency={changeCurrency}
        view={view}
        onView={setView}
      />

      <TimelineWindowNav
        fromMs={fromMs}
        toMs={toMs}
        stepMs={stepMs}
        onStep={setStepMs}
        onShift={shift}
        canShiftRight={toMs < Date.now() - 1000}
        loading={loading}
      />

      {error && (
        <div className='alert alert-danger d-flex align-items-center justify-content-between'>
          <span>{error}</span>
          <button
            type='button'
            className='btn btn-sm btn-light-danger'
            onClick={load}
            disabled={loading}
          >
            Повторить
          </button>
        </div>
      )}
      {!data && loading && (
        <div className='text-muted d-inline-flex align-items-center gap-2'>
          <span className='spinner-border spinner-border-sm' role='status' /> Загрузка…
        </div>
      )}
      {data && (
        <TimelineCanvas
          fromMs={fromMs}
          toMs={toMs}
          payouts={payouts}
          deposits={deposits}
          merchants={data.merchants}
          methods={data.payment_methods}
          loading={loading}
        />
      )}
    </>
  )
}

export default DepositPayoutTimelinePage
